// ----- Phase 5.X-publish-governance — Frontend orchestrator -----
//
// Pure-function state machine that decides which publish-governance
// modal (if any) should be visible NOW given:
//
//   - Publish governance (`preP`, `reAuth`) — resolved by the BACKEND for
//     this submission (own company profile → org setting → false) and served
//     by `GET /submissions/:id/prepublish-checklist` (see
//     `usePublishGovernance`). T-17 (T17-D2): the page no longer reads the
//     org settings itself.
//   - Submission context (`unplacedFormsCount`, `outOfSequenceOrder`)
//     — derived from the latest validation results / submission row.
//   - Accumulated flow state (which step we're on, what the user has
//     answered so far).
//
// The orchestrator is intentionally separate from the modal components
// so we can unit-test the gating logic without rendering anything.
// `CompileSubmissionPanel` reduces over `nextStep` to drive its UI.
//
// Mirrors the backend gating logic in
// `src/services/publishGovernance/publishGovernance.ts` — if you change
// the order of modals here, also adjust the backend's expectation about
// which fields it requires when.

export type PublishGovernanceStep =
  /** Compile clicked but the orchestrator hasn't decided what to show yet. */
  | 'idle'
  /** Pre-publish checklist modal is open. */
  | 'checklist'
  /** E-signature re-auth modal is open. */
  | 'esig'
  /** Test-vs-final radio modal is open. */
  | 'intent'
  /** Unplaced-forms warning is open (skipped when zero unplaced). */
  | 'unplaced'
  /** Sequence-order warning is open (skipped when in order). */
  | 'seqorder'
  /** All gating done; trigger the actual compile call. */
  | 'compile'
  /** Operator cancelled at any modal; back to idle UI, no compile call. */
  | 'cancelled';

export interface PublishGovernanceSettings {
  /** Resolved `preP` (pre-publish checklist required). */
  readonly preP: boolean;
  /** Resolved `reAuth` (e-signature re-auth required). */
  readonly reAuth: boolean;
  /**
   * T-17 (T17-D14) — ids of the manual items the SERVER requires acknowledged
   * (the required manual items of the served checklist). The checklist step is
   * done only when every one of them is `true` in the ack map. Absent ⇒ the
   * required set is unknown, so the checklist is never treated as done
   * (fail closed — the ids are the server's, the web declares none).
   */
  readonly requiredAckIds?: readonly string[];
}

export interface SubmissionPublishContext {
  /** Admin forms with no eCTD section placement. 0 → unplaced modal skipped. */
  readonly unplacedFormsCount: number;
  /**
   * True iff this submission's sequence # is NOT the lowest unlocked
   * for its NDA (i.e. publishing would skip an earlier sequence). When
   * false, the seq-order modal is skipped.
   */
  readonly outOfSequenceOrder: boolean;
}

export interface SignatureAttestation {
  readonly signerOid: string;
  readonly signerEmail: string;
  readonly signatureTimestampUtc: string;
  readonly signatureMeaning: 'approval-for-publication';
  readonly attestationToken: string;
}

export interface PublishGovernanceFlowState {
  readonly step: PublishGovernanceStep;
  readonly publishMode: 'test' | 'final' | null;
  readonly signatureAttestation: SignatureAttestation | null;
  readonly checklistAcknowledgments: Readonly<Record<string, boolean>>;
  readonly unplacedFormsAck: boolean;
  readonly seqOrderAck: boolean;
}

/** Initial state when the operator clicks Compile. */
export function initialFlowState(): PublishGovernanceFlowState {
  return {
    step: 'idle',
    publishMode: null,
    signatureAttestation: null,
    checklistAcknowledgments: {},
    unplacedFormsAck: false,
    seqOrderAck: false,
  };
}

/**
 * Decide the next step. Pure function — no side effects, no state held
 * outside the args. Tests hammer it directly with synthetic combinations.
 *
 * The order matters: checklist → esig → intent → unplaced → seqorder →
 * compile. Each step short-circuits to the next when the gating
 * condition isn't met. `cancelled` is a terminal state set externally.
 */
export function nextStep(
  state: PublishGovernanceFlowState,
  settings: PublishGovernanceSettings,
  context: SubmissionPublishContext,
): PublishGovernanceStep {
  if (state.step === 'cancelled') return 'cancelled';
  if (state.step === 'compile') return 'compile';

  // 1. Checklist (skipped if preP off OR already acknowledged)
  const checklistRequired = settings.preP;
  const checklistDone = checklistAlreadyDone(
    state.checklistAcknowledgments,
    settings.requiredAckIds,
  );
  if (checklistRequired && !checklistDone) return 'checklist';

  // 2. E-sig re-auth (skipped if reAuth off OR already captured)
  const esigRequired = settings.reAuth;
  const esigDone = state.signatureAttestation !== null;
  if (esigRequired && !esigDone) return 'esig';

  // 3. Publish intent — always required (Test vs Final).
  if (state.publishMode === null) return 'intent';

  // 4. Unplaced forms (skipped if zero unplaced OR already acknowledged)
  if (context.unplacedFormsCount > 0 && !state.unplacedFormsAck) {
    return 'unplaced';
  }

  // 5. Sequence-order (skipped if in order OR already acknowledged)
  if (context.outOfSequenceOrder && !state.seqOrderAck) {
    return 'seqorder';
  }

  return 'compile';
}

/**
 * T-17 (T17-D14) — done iff EVERY required id is acknowledged `true`. The old
 * rule ("some key exists and none is false") called `{metadata: true}` complete
 * while the server needs all three. Unknown required set ⇒ not done.
 */
function checklistAlreadyDone(
  ack: Readonly<Record<string, boolean>>,
  requiredIds: readonly string[] | undefined,
): boolean {
  if (requiredIds === undefined) return false;
  return requiredIds.every((id) => ack[id] === true);
}

// ----- State transitions ----- //

export function applyChecklistComplete(
  state: PublishGovernanceFlowState,
  acks: Record<string, boolean>,
): PublishGovernanceFlowState {
  return { ...state, checklistAcknowledgments: { ...acks } };
}

export function applySignatureCaptured(
  state: PublishGovernanceFlowState,
  attestation: SignatureAttestation,
): PublishGovernanceFlowState {
  return { ...state, signatureAttestation: attestation };
}

export function applyPublishMode(
  state: PublishGovernanceFlowState,
  mode: 'test' | 'final',
): PublishGovernanceFlowState {
  return { ...state, publishMode: mode };
}

export function applyUnplacedFormsAck(
  state: PublishGovernanceFlowState,
): PublishGovernanceFlowState {
  return { ...state, unplacedFormsAck: true };
}

export function applySeqOrderAck(
  state: PublishGovernanceFlowState,
): PublishGovernanceFlowState {
  return { ...state, seqOrderAck: true };
}

export function applyCancel(
  state: PublishGovernanceFlowState,
): PublishGovernanceFlowState {
  return { ...state, step: 'cancelled' };
}

// ----- Checklist items ----- //
//
// T-17 (T17-D14): the web declares NO item list. The backend
// (`src/services/publishGovernance/checklistItems.ts`) owns the ids and labels
// and serves them — evaluated auto items first, then the manual items — from
// GET /submissions/:id/prepublish-checklist. This is the shape the wizard and
// the legacy modal render.

export interface ChecklistItem {
  readonly id: string;
  readonly label: string;
  /** Blocks publish when failing. False ⇒ warning-only, never gates compile. */
  readonly required: boolean;
  readonly kind: 'manual' | 'auto';
  /** Auto: pass/fail/unknown. Manual: 'manual'. */
  readonly state: 'pass' | 'fail' | 'unknown' | 'manual';
  /** Auto verdict; `null` when manual or not evaluated. */
  readonly passed: boolean | null;
  /** Server-supplied explanation (failure reason / why unknown). */
  readonly detail?: string;
  readonly failureMessage?: string | null;
}

/** Ids of the served manual items the operator must acknowledge. */
export function requiredAckIdsOf(items: ReadonlyArray<ChecklistItem>): string[] {
  return items.filter((i) => i.kind === 'manual' && i.required).map((i) => i.id);
}
